import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, BookOpenIcon, ShoppingBagIcon, HeartIcon, StarIcon, BookmarkIcon, CalendarIcon, MessageSquareIcon, SettingsIcon, LogOutIcon, StoreIcon, LayoutDashboardIcon, PlusCircleIcon, ClipboardListIcon, TruckIcon, LineChartIcon, PercentIcon, HelpCircleIcon, DollarSignIcon, PackageIcon, UsersIcon, MegaphoneIcon, CreditCardIcon, ListChecksIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface UserDropdownProps {
  isReader: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  userButtonRef: React.RefObject<HTMLButtonElement>;
}
const UserDropdown = ({
  isReader,
  isOpen,
  setIsOpen,
  userButtonRef
}: UserDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dk = useDarkClasses();
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && userButtonRef.current && !userButtonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen, userButtonRef]);
  // Close dropdown when pressing Escape
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [setIsOpen]);
  if (!isOpen) return null;
  // Dynamic classes using dk hooks
  const dropdownBg = dk.isDark ? 'bg-gray-800' : 'bg-white';
  const headerBg = dk.isDark ? 'bg-gray-800' : 'bg-white';
  const borderColor = dk.isDark ? 'border-gray-700' : 'border-gray-100';
  const textPrimary = dk.textPrimary;
  const textSecondary = dk.isDark ? 'text-gray-300' : 'text-gray-700';
  const textMuted = dk.textMuted;
  // Role-specific hover states
  const readerHover = dk.isDark ? 'hover:bg-indigo-900/50 hover:text-indigo-300' : 'hover:bg-indigo-50';
  const vendorHover = dk.isDark ? 'hover:bg-purple-900/50 hover:text-purple-300' : 'hover:bg-purple-50';
  // Role-specific accent colors
  const readerAccent = dk.isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-500 hover:text-indigo-600';
  const vendorAccent = dk.isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-800 hover:text-purple-900';
  return <div ref={dropdownRef} className={`absolute right-0 w-64 mt-2 ${dropdownBg} rounded-lg shadow-lg max-h-[80vh] overflow-y-auto z-50 ${dk.isDark ? 'ring-1 ring-gray-700' : ''}`} role="menu" aria-orientation="vertical" aria-labelledby="user-menu-button" data-id="element-2872" data-mp="oyeywquyth96">
      {isReader ? <>
          {/* Reader Menu */}
          <div className={`px-4 py-2 border-b ${borderColor} sticky top-0 ${headerBg} z-10`} data-id="element-2873" data-mp="2x8mp7x8ytb5n">
            <div className="flex items-center space-x-3" data-id="element-2874" data-mp="3bvfx4hg5nent">
              <div className={`h-10 w-10 rounded-full ${dk.isDark ? 'bg-indigo-900/50' : 'bg-indigo-100'} flex items-center justify-center`} data-id="element-2875" data-mp="36xwj9indd3we">
                <UserIcon className={`h-6 w-6 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-500'}`} data-id="element-2876" data-mp="2ja6bvnglkmej" />
              </div>
              <div data-id="element-2877" data-mp="2s9t9rza4ba2u">
                <div className={`font-medium ${textPrimary}`} data-id="element-2878" data-mp="2sabfrnml12ny">
                  Reader Account
                </div>
                <Link to="/profile" className={`text-sm ${readerAccent}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2879" data-mp="2o99g55or6olg">
                  View Profile
                </Link>
              </div>
            </div>
          </div>
          <div className="py-2" data-id="element-2880" data-mp="12spp4kjbqzno">
            <Link to="/my-bookshelf" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2881" data-mp="12tqzsnwfhk4v">
              <BookOpenIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2882" data-mp="1cosojimskk6x" />
              My Bookshelf
            </Link>
            <Link to="/my-orders" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2883" data-mp="1ho5kcsvn51ln">
              <ShoppingBagIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2884" data-mp="1vhmtdjqpavxf" />
              My Orders
            </Link>
            <Link to="/wishlist" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2885" data-mp="20hj21wacfbjb">
              <HeartIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2886" data-mp="3pvil5tbrskhu" />
              My Wishlist
            </Link>
            <Link to="/my-reviews" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2887" data-mp="3l0v42fj3isda">
              <StarIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2888" data-mp="ciyq4vla8lhh" />
              My Reviews
            </Link>
            <Link to="/saved-events" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2889" data-mp="7kngakuzmaz9">
              <ListChecksIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2890" data-mp="3tx43hu72pqzf" />
              My Tracker
            </Link>
            <Link to="/messages" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2891" data-mp="qigifkxsegvl">
              <MessageSquareIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2892" data-mp="2ppokt0qpabpr" />
              Messages
            </Link>
          </div>
          <div className={`border-t ${borderColor} pt-2`} data-id="element-2893" data-mp="2azzdens0lbh1">
            <Link to="/account-settings" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2894" data-mp="2fytwlqtw921o">
              <SettingsIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2895" data-mp="262qxktuc9jiy" />
              Account Settings
            </Link>
            <Link to="/account/subscriptions" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2896" data-mp="1s3zmlgarwdz1">
              <CreditCardIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2897" data-mp="3rdpkm3efkfuw" />
              Manage Subscriptions
            </Link>
            <button onClick={() => {
          setIsOpen(false);
        }} className={`w-full flex items-center px-4 py-2 text-sm ${textSecondary} ${readerHover}`} role="menuitem" data-id="element-2898" data-mp="1frx991emhayy">
              <LogOutIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2899" data-mp="1kqrvyefpnjoc" />
              Logout
            </button>
          </div>
        </> : <>
          {/* Vendor Menu */}
          <div className={`px-4 py-2 border-b ${borderColor} sticky top-0 ${headerBg} z-10`} data-id="element-2900" data-mp="1titug767ypvz">
            <div className="flex items-center space-x-3" data-id="element-2901" data-mp="28gm80ts7h47j">
              <div className={`h-10 w-10 rounded-full ${dk.isDark ? 'bg-purple-900/50' : 'bg-purple-100'} flex items-center justify-center`} data-id="element-2902" data-mp="28e018wd52sqd">
                <StoreIcon className={`h-6 w-6 ${dk.isDark ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-2903" data-mp="e2lcahrycxbo" />
              </div>
              <div data-id="element-2904" data-mp="3jfp6vi7ijtwx">
                <div className={`font-medium ${textPrimary}`} data-id="element-2905" data-mp="3je5qlrluxiv5">
                  Vendor Account
                </div>
                <Link to="/vendor-profile" className={`text-sm ${vendorAccent}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2906" data-mp="3ocpc5qwjgsmz">
                  View Profile
                </Link>
              </div>
            </div>
          </div>
          <div className="py-2" data-id="element-2907" data-mp="30ifirnr8ts9c">
            <Link to="/dashboard" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2908" data-mp="35ew82la6ico0">
              <LayoutDashboardIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2909" data-mp="3ehno6ye01wlz" />
              Dashboard
            </Link>
            <Link to="/vendors/manage" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2910" data-mp="pqcpuh69aya4">
              <PlusCircleIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2911" data-mp="2a5soki8qn3ie" />
              Add New Listing
            </Link>
            <Link to="/manage-listings" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2912" data-mp="25ekedjzl3e46">
              <ClipboardListIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2913" data-mp="1vfbob27uajn3" />
              Manage Listings
            </Link>
            <Link to="/orders" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2914" data-mp="1mgghbnwz15iq">
              <TruckIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2915" data-mp="3gtpw9v8adj8d" />
              Orders & Shipping
            </Link>
            <Link to="/vendors/preorders" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2916" data-mp="3bw7jtuv28lvm">
              <PackageIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2917" data-mp="1dw0dzseoxamo" />
              Pre-Orders
            </Link>
            <Link to="/vendors/arc" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2918" data-mp="18we3hqq8y8hk">
              <UsersIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2919" data-mp="h71a0pl52v0a" />
              ARC Management
            </Link>
            <Link to="/vendors/favorites" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2920" data-mp="uyfs8u782s6a">
              <HeartIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2921" data-mp="3tnena6w4t82y" />
              My Favorites
            </Link>
          </div>
          <div className={`border-t ${borderColor} pt-2`} data-id="element-2922" data-mp="2941ahylxj1hn">
            <div className={`px-4 py-1 text-xs font-semibold uppercase ${textMuted}`} data-id="element-2923" data-mp="2dvsvzuq8ah0x">
              Analytics & Marketing
            </div>
            <Link to="/analytics" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2924" data-mp="2068ustcaprtq">
              <LineChartIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2925" data-mp="2wuet6yimjnzk" />
              Analytics
            </Link>
            <Link to="/vendors/royalties" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2926" data-mp="2ivf984623x9q">
              <DollarSignIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2927" data-mp="17efe70sbr4c0" />
              Royalties
            </Link>
            <Link to="/vendors/insights" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2928" data-mp="1ccqsbnpnk101">
              <UsersIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2929" data-mp="jjltwf15wqnm" />
              Reader Insights
            </Link>
            <Link to="/vendors/marketing" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2930" data-mp="ek94bmiwp5hc">
              <MegaphoneIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2931" data-mp="4phamtsg32ru" />
              Marketing Tools
            </Link>
            <Link to="/promotions" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2932" data-mp="3rs3v5hquhxls">
              <PercentIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2933" data-mp="1q1sgw5idmksk" />
              Promotions
            </Link>
            <Link to="/manage-events" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2934" data-mp="1xjguk5su0ui6">
              <CalendarIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2935" data-mp="2c8wile0zalu6" />
              Manage Events
            </Link>
          </div>
          <div className={`border-t ${borderColor} pt-2`} data-id="element-2936" data-mp="1elxlx3aaddwa">
            <Link to="/account-settings" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2937" data-mp="1engxwhowt2s9">
              <SettingsIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2938" data-mp="1slyjo9v9857j" />
              Account Settings
            </Link>
            <Link to="/account/subscriptions" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2939" data-mp="2zxxksjj7x0r0">
              <CreditCardIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2940" data-mp="2xk2627ebitwm" />
              Manage Subscriptions
            </Link>
            <Link to="/support" className={`flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-2941" data-mp="3chcpq414763f">
              <HelpCircleIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2942" data-mp="3lfy73vuuof2m" />
              Help Center
            </Link>
            <button onClick={() => {
          setIsOpen(false);
        }} className={`w-full flex items-center px-4 py-2 text-sm ${textSecondary} ${vendorHover}`} role="menuitem" data-id="element-2943" data-mp="3gglhx4giavwv">
              <LogOutIcon className={`h-4 w-4 mr-3 ${textMuted}`} data-id="element-2944" data-mp="8234en7sqo5v" />
              Logout
            </button>
          </div>
        </>}
    </div>;
};
export default UserDropdown;